/**
 * Model veřejné dopravy: síť zastávek a linek, vyhledání spojení
 * s čekáním a přestupy (časově závislé, dle jízdního řádu) a rychlý
 * očekávaný model pro hromadné zónové výpočty.
 */
import { destinationPoint, bearingDeg, haversineKm, lerpPoint } from './geo';
import {
  expectedWaitAtMin,
  expectedWaitMin,
  lineDepartureMinutes,
  msFromPragueLocalCached,
  nextDeparture,
  pragueDateCached,
  pragueMinutesOfDay,
} from './time';
import type { LatLng, TransitLine, TransitNetwork, TransitStop } from './types';

export const VEHICLE_LABEL: Record<string, string> = {
  bus: 'Bus',
  tram: 'Tram',
  metro: 'Metro',
  train: 'Vlak',
  ferry: 'Přívoz',
};

/** Názvy ikon (viz src/ui/icons.ts) pro typy vozidel. */
export const VEHICLE_ICON: Record<string, string> = {
  bus: 'bus',
  tram: 'tram',
  metro: 'train',
  train: 'train',
  ferry: 'ferry',
};

/* ------------------------------------------------------------------ */
/* Demo síť                                                            */
/* ------------------------------------------------------------------ */

const STOP_NAMES = [
  'Nádraží', 'Náměstí', 'U Parku', 'Škola', 'Stadion', 'Nemocnice', 'U Mostu',
  'Sídliště', 'Vodárna', 'Divadlo', 'Knihovna', 'U Lípy', 'Tržnice', 'Zahrady',
  'Pivovar', 'Kostel', 'U Rybníka', 'Výhledy', 'Brána', 'Dvůr', 'Mlýn', 'Lom',
  'Polní', 'Vyhlídka', 'Přístav', 'Kasárna', 'Obora', 'Souhvězdí', 'Lužiny', 'Habrová',
];

/** Deterministický pseudonáhodný generátor (mulberry32) — stabilní demo síť pro dané A/B. */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Vytvoří věrohodnou demo síť MHD kolem koridoru A–B.
 * Je deterministická (stejná A/B → stejná síť) a transparentně označená
 * jako odhad. Slouží jako fallback, když uživatel nezadá vlastní linky.
 */
export function buildDemoNetwork(a: LatLng, b: LatLng): TransitNetwork {
  const dist = haversineKm(a, b);
  const empty: TransitNetwork = {
    stops: [],
    lines: [],
    maxAccessKm: 1.2,
    transferMin: 3,
    source: 'demo',
  };
  if (dist < 0.4 || dist > 400) return empty; // moc krátké / extrémní — MHD nedává smysl

  const seed = Math.round((a.lat * 7919 + a.lng * 104729 + b.lat * 1299709 + b.lng * 15485863) * 1000);
  const rand = rng(seed);
  const nameAt = (i: number): string =>
    STOP_NAMES[i % STOP_NAMES.length] + (i >= STOP_NAMES.length ? ` ${Math.floor(i / STOP_NAMES.length) + 1}` : '');

  const stops: TransitStop[] = [];
  const lines: TransitLine[] = [];
  let stopSeq = 0;
  let nameSeq = Math.floor(rand() * STOP_NAMES.length);

  const addStop = (p: LatLng): TransitStop => {
    const s: TransitStop = {
      id: `demo-s${stopSeq++}`,
      name: nameAt(nameSeq++),
      lat: p.lat,
      lng: p.lng,
    };
    stops.push(s);
    return s;
  };

  const brg = bearingDeg(a, b);
  const perp = (brg + 90) % 360;

  // Dálkový scénář: jedna „vlaková" linka s řidšími zastávkami.
  const longHaul = dist > 25;
  const trunkSpacing = longHaul ? Math.max(6, dist / 7) : Math.max(0.55, Math.min(1.0, dist / 7));
  const trunkSpeed = longHaul ? 80 : dist > 6 ? 32 : 20;
  const vehicle = longHaul ? 'train' : dist > 6 ? 'metro' : 'tram';

  // Páteřní linka podél koridoru, mírně zvlněná, přesahuje A i B.
  const trunkIds: string[] = [];
  const over = trunkSpacing * 0.8;
  const total = dist + 2 * over;
  const nTrunk = Math.min(24, Math.max(3, Math.round(total / trunkSpacing) + 1));
  const start = destinationPoint(a, (brg + 180) % 360, over);
  for (let i = 0; i < nTrunk; i++) {
    const along = (total * i) / (nTrunk - 1);
    const base = destinationPoint(start, brg, along);
    const wobble = Math.sin(i * 1.7 + rand() * 3) * Math.min(0.35, dist * 0.03);
    const p = wobble !== 0 ? destinationPoint(base, perp, wobble) : base;
    trunkIds.push(addStop(p).id);
  }
  lines.push({
    id: 'demo-trunk',
    name: longHaul ? 'R1' : dist > 6 ? 'M1' : 'T1',
    vehicle,
    color: '#d6293a',
    stopIds: trunkIds,
    headwayMin: longHaul ? 30 : dist > 6 ? 5 : 8,
    firstDep: '04:50',
    lastDep: longHaul ? '22:30' : '23:40',
    speedKmh: trunkSpeed,
    dwellMin: longHaul ? 1.5 : 0.5,
    costFlat: longHaul ? Math.round(40 + dist * 1.6) : 30,
    emissionsPerKm: longHaul ? 30 : 35,
    comfort: longHaul ? 4 : 3,
    accessible: true,
    bidirectional: true,
  });

  // Příčné napaječe (jen pro městská měřítka) — kříží koridor ve ⅓ a ⅔.
  if (!longHaul && dist >= 1.2) {
    const feeders: Array<{ t: number; name: string; color: string }> = [
      { t: 0.3, name: '12', color: '#1d7ff0' },
      { t: 0.68, name: '23', color: '#0b8f4d' },
    ];
    for (const f of feeders) {
      const center = lerpPoint(a, b, f.t);
      const ids: string[] = [];
      const half = Math.min(4, Math.max(2, Math.round(dist / 2)));
      const spacing = Math.max(0.45, Math.min(0.8, dist / 8));
      for (let i = -half; i <= half; i++) {
        if (i === 0) {
          // Sdílená přestupní zastávka s páteří, pokud je poblíž.
          let nearest: TransitStop | null = null;
          let best = Infinity;
          for (const sid of trunkIds) {
            const s = stops.find((x) => x.id === sid)!;
            const d = haversineKm(s, center);
            if (d < best) {
              best = d;
              nearest = s;
            }
          }
          if (nearest && best < spacing * 0.7) {
            ids.push(nearest.id);
            continue;
          }
        }
        const angle = i < 0 ? (perp + 180) % 360 : perp;
        const p = destinationPoint(center, angle, Math.abs(i) * spacing);
        ids.push(addStop(p).id);
      }
      lines.push({
        id: `demo-feeder-${f.name}`,
        name: f.name,
        vehicle: 'bus',
        color: f.color,
        stopIds: ids,
        headwayMin: 10 + Math.round(rand() * 5),
        firstDep: '05:10',
        lastDep: '23:00',
        speedKmh: 21,
        dwellMin: 0.4,
        costFlat: 30,
        emissionsPerKm: 60,
        comfort: 3,
        accessible: rand() > 0.3,
        bidirectional: true,
      });
    }
  }

  return {
    stops,
    lines,
    maxAccessKm: longHaul ? 8 : 1.2,
    transferMin: 3,
    source: 'demo',
  };
}

/* ------------------------------------------------------------------ */
/* Pomocné struktury nad sítí                                          */
/* ------------------------------------------------------------------ */

export interface LineRun {
  line: TransitLine;
  /** Zastávky v pořadí jízdy tohoto směru. */
  stops: TransitStop[];
  /** Kumulativní čas od výchozí zastávky směru (min). */
  offsetsMin: number[];
  /** Kumulativní vzdálenost (km). */
  offsetsKm: number[];
  dir: 1 | -1;
}

/** Rozvine linky na jednosměrné „runy" s kumulativními časy. */
export function buildRuns(net: TransitNetwork): LineRun[] {
  const byId = new Map(net.stops.map((s) => [s.id, s]));
  const runs: LineRun[] = [];
  for (const line of net.lines) {
    const seq = line.stopIds.map((id) => byId.get(id)).filter((s): s is TransitStop => !!s);
    if (seq.length < 2) continue;
    const dirs: Array<[TransitStop[], 1 | -1]> = [[seq, 1]];
    if (line.bidirectional) dirs.push([[...seq].reverse(), -1]);
    for (const [stopsDir, dir] of dirs) {
      const offsetsMin = [0];
      const offsetsKm = [0];
      for (let i = 1; i < stopsDir.length; i++) {
        const d = haversineKm(stopsDir[i - 1], stopsDir[i]) * 1.15; // mírná nepřímost kolejí/ulic
        offsetsKm.push(offsetsKm[i - 1] + d);
        offsetsMin.push(offsetsMin[i - 1] + (d / line.speedKmh) * 60 + line.dwellMin);
      }
      runs.push({ line, stops: stopsDir, offsetsMin, offsetsKm, dir });
    }
  }
  return runs;
}

/* ------------------------------------------------------------------ */
/* Přesné vyhledání spojení (časově závislé)                            */
/* ------------------------------------------------------------------ */

export interface PtLeg {
  kind: 'access' | 'wait' | 'ride' | 'transfer' | 'egress';
  line?: TransitLine;
  fromStop?: TransitStop;
  toStop?: TransitStop;
  /** Zastávky projeté na úseku (pro geometrii). */
  viaStops?: TransitStop[];
  startMs: number;
  endMs: number;
  distanceKm: number;
}

export interface PtJourney {
  legs: PtLeg[];
  departMs: number;
  arriveMs: number;
  waitMin: number;
  rideKm: number;
  accessKm: number;
  egressKm: number;
  transfers: number;
  /** Linky použité na trase. */
  linesUsed: TransitLine[];
  accessible: boolean;
}

interface Label {
  stopIdx: number;
  boardings: number;
  timeMs: number;
  waitMin: number;
  prev: Label | null;
  via:
    | { type: 'access'; km: number }
    | { type: 'walk'; fromIdx: number; km: number }
    | {
        type: 'ride';
        run: LineRun;
        boardIdx: number; // index v run.stops
        alightIdx: number;
        depMs: number;
        waitBefore: number;
      }
    | null;
}

const MAX_BOARDINGS = 3;
const WALK_TRANSFER_KM = 0.35;

/* ------------------------------------------------------------------ */
/* Prostorový index zastávek (mřížkové koše)                            */
/*                                                                      */
/* U reálných sítí (PID ~3 400 zastávek) by O(n²) párování zastávek     */
/* bylo ~11M výpočtů na každý přepočet. Koše ~2 km srazí hledání        */
/* sousedů na O(n × konstanta).                                         */
/* ------------------------------------------------------------------ */

const BUCKET_DEG = 0.02; // ~2,2 km

type StopIndex = Map<string, number[]>;

function buildStopIndex(stops: TransitStop[]): StopIndex {
  const index: StopIndex = new Map();
  for (let i = 0; i < stops.length; i++) {
    const key = `${Math.floor(stops[i].lat / BUCKET_DEG)}:${Math.floor(stops[i].lng / BUCKET_DEG)}`;
    const arr = index.get(key);
    if (arr) arr.push(i);
    else index.set(key, [i]);
  }
  return index;
}

/** Indexy zastávek do vzdálenosti radiusKm od bodu (přes okolní koše). */
function nearbyStops(p: LatLng, stops: TransitStop[], index: StopIndex, radiusKm: number): number[] {
  const degRadius = radiusKm / 95; // konzervativně (lat i lng v ČR)
  const span = Math.max(1, Math.ceil(degRadius / BUCKET_DEG));
  const cLat = Math.floor(p.lat / BUCKET_DEG);
  const cLng = Math.floor(p.lng / BUCKET_DEG);
  const out: number[] = [];
  for (let dy = -span; dy <= span; dy++) {
    for (let dx = -span; dx <= span; dx++) {
      const arr = index.get(`${cLat + dy}:${cLng + dx}`);
      if (arr) out.push(...arr);
    }
  }
  return out;
}

/**
 * Najde nejrychlejší spojení MHD z origin do destination po čase departMs.
 * Přístup na zastávku jede rychlostí accessSpeed (pěšky / kolo / auto u P+R),
 * výstup vždy chůzí (walkSpeed). Vrací null, pokud spojení neexistuje.
 *
 * Algoritmus: časově závislá Dijkstra nad stavy (zastávka, počet nástupů)
 * s nástupem na nejbližší odjezd dle jízdního řádu — efektivní pro stovky
 * zastávek a připravená na více linek a přestupů.
 */
/**
 * Předpočítané struktury sítě pro vyhledávání — staví se JEDNOU per síť
 * (WeakMap dle identity zastávkového pole) a sdílí napříč přepočty.
 * U PID (~5 000 zastávek) šetří stovky ms na každém plánování.
 */
interface NetPrep {
  runs: LineRun[];
  idxOf: Map<string, number>;
  runsAtStop: Array<Array<{ run: LineRun; pos: number }>>;
  walkEdges: Array<Array<{ to: number; km: number }>>;
  spatial: StopIndex;
  /** Sdílené cache jízdních řádů (přežívají mezi přepočty). */
  lineDeps: Map<string, number[]>;
  depsAtPos: Map<string, number[]>;
  runKeys: Map<LineRun, string>;
}

const netPrepCache = new WeakMap<object, NetPrep>();

function prepareNet(net: TransitNetwork): NetPrep | null {
  const cached = netPrepCache.get(net.stops);
  if (cached) return cached;
  const runs = buildRuns(net);
  if (runs.length === 0) return null;
  const stops = net.stops;
  const idxOf = new Map(stops.map((s, i) => [s.id, i]));

  // Runy obsluhující zastávku (kromě poslední zastávky směru).
  const runsAtStop: Array<Array<{ run: LineRun; pos: number }>> = stops.map(() => []);
  for (const run of runs) {
    run.stops.forEach((s, pos) => {
      if (pos < run.stops.length - 1) {
        const i = idxOf.get(s.id);
        if (i !== undefined) runsAtStop[i].push({ run, pos });
      }
    });
  }

  // Pěší přestupové hrany mezi blízkými zastávkami (přes prostorový index).
  const spatial = buildStopIndex(stops);
  const walkEdges: Array<Array<{ to: number; km: number }>> = stops.map(() => []);
  for (let i = 0; i < stops.length; i++) {
    for (const j of nearbyStops(stops[i], stops, spatial, WALK_TRANSFER_KM)) {
      if (j <= i) continue;
      const km = haversineKm(stops[i], stops[j]);
      if (km > 0 && km <= WALK_TRANSFER_KM) {
        walkEdges[i].push({ to: j, km });
        walkEdges[j].push({ to: i, km });
      }
    }
  }

  const runKeys = new Map<LineRun, string>();
  runs.forEach((r, i) => runKeys.set(r, `${i}`));
  const prep: NetPrep = {
    runs,
    idxOf,
    runsAtStop,
    walkEdges,
    spatial,
    lineDeps: new Map(),
    depsAtPos: new Map(),
    runKeys,
  };
  netPrepCache.set(net.stops, prep);
  return prep;
}

export function findJourney(
  origin: LatLng,
  destination: LatLng,
  departMs: number,
  net: TransitNetwork,
  opts: {
    accessSpeedKmh: number;
    accessCircuity: number;
    walkSpeedKmh: number;
    walkCircuity: number;
    /** Omezit přístup k zastávce (km vzdušně); výchozí dle sítě. */
    maxAccessKm?: number;
    maxTransfers?: number;
  },
): PtJourney | null {
  if (net.stops.length === 0 || net.lines.length === 0) return null;
  const prep = prepareNet(net);
  if (!prep) return null;
  const { idxOf, runsAtStop, walkEdges } = prep;

  const stops = net.stops;
  const maxBoard = Math.min(MAX_BOARDINGS, (opts.maxTransfers ?? 2) + 1);
  const maxAccess = opts.maxAccessKm ?? net.maxAccessKm;

  const accessMinPerKm = (60 / opts.accessSpeedKmh) * opts.accessCircuity;
  const walkMinPerKm = (60 / opts.walkSpeedKmh) * opts.walkCircuity;

  // Jízdní řády po linkách jen jednou (parsování/generování je drahé)
  // a normalizované+seřazené odjezdy per (run, pozice) — sdílené cache
  // z NetPrep přežívají i mezi přepočty.
  const { lineDeps, depsAtPos, runKeys } = prep;

  // Rychlé hledání nejbližšího odjezdu: u dnů BEZ změny času (99 % roku)
  // platí ms = půlnoc + minuty, takže stačí binární hledání v seřazených
  // odjezdech. DST dny jdou pomalou přesnou cestou (nextDeparture).
  const dayInfo = new Map<number, { startMs: number; simple: boolean; y: number; m: number; d: number }>();
  const fastNextDeparture = (readyMs: number, sortedDeps: number[]): number | null => {
    if (sortedDeps.length === 0) return null;
    const p = pragueDateCached(readyMs);
    const dayKey = (p.year * 13 + p.month) * 32 + p.day;
    let info = dayInfo.get(dayKey);
    if (!info) {
      const startMs = msFromPragueLocalCached(p.year, p.month, p.day, 0, 0);
      const nextStartMs = msFromPragueLocalCached(p.year, p.month, p.day + 1, 0, 0);
      info = { startMs, simple: nextStartMs - startMs === 86_400_000, y: p.year, m: p.month, d: p.day };
      dayInfo.set(dayKey, info);
    }
    if (!info.simple) return nextDeparture(readyMs, sortedDeps, true);
    const minutes = (readyMs - info.startMs) / 60_000 - 0.5; // tolerance ~30 s jako přesná cesta
    let lo = 0;
    let hi = sortedDeps.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (sortedDeps[mid] < minutes) lo = mid + 1;
      else hi = mid;
    }
    if (lo < sortedDeps.length) return info.startMs + sortedDeps[lo] * 60_000;
    // Dnes už nic nejede → první zítřejší spoj (přesný převod kvůli DST zítřka).
    const dep0 = sortedDeps[0];
    return msFromPragueLocalCached(info.y, info.m, info.d + 1, Math.floor(dep0 / 60), dep0 % 60);
  };

  /** Rotace seřazených odjezdů o offset (mod 1440) bez dalšího řazení — O(n). */
  const rotateDeps = (sortedBase: number[], offset: number): number[] => {
    const off = ((offset % 1440) + 1440) % 1440;
    if (off === 0) return sortedBase;
    const split = 1440 - off; // hodnoty ≥ split se přetočí na začátek dne
    let lo = 0;
    let hi = sortedBase.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (sortedBase[mid] < split) lo = mid + 1;
      else hi = mid;
    }
    const out = new Array<number>(sortedBase.length);
    let k = 0;
    for (let i = lo; i < sortedBase.length; i++) out[k++] = sortedBase[i] + off - 1440;
    for (let i = 0; i < lo; i++) out[k++] = sortedBase[i] + off;
    return out;
  };

  // best[stop][boardings] = nejlepší čas; otevřené uzly v binární haldě.
  const best: number[][] = stops.map(() => new Array(maxBoard + 1).fill(Infinity));
  const open: Label[] = [];
  const heapUp = (i: number): void => {
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (open[parent].timeMs <= open[i].timeMs) break;
      [open[parent], open[i]] = [open[i], open[parent]];
      i = parent;
    }
  };
  const heapDown = (i: number): void => {
    for (;;) {
      const l = 2 * i + 1;
      const r = l + 1;
      let m = i;
      if (l < open.length && open[l].timeMs < open[m].timeMs) m = l;
      if (r < open.length && open[r].timeMs < open[m].timeMs) m = r;
      if (m === i) break;
      [open[m], open[i]] = [open[i], open[m]];
      i = m;
    }
  };
  const popMin = (): Label => {
    const top = open[0];
    const last = open.pop()!;
    if (open.length > 0) {
      open[0] = last;
      heapDown(0);
    }
    return top;
  };
  const push = (l: Label): void => {
    if (l.timeMs >= best[l.stopIdx][l.boardings]) return;
    best[l.stopIdx][l.boardings] = l.timeMs;
    open.push(l);
    heapUp(open.length - 1);
  };

  // Počáteční přístupy na zastávky.
  for (let i = 0; i < stops.length; i++) {
    const km = haversineKm(origin, stops[i]);
    if (km <= maxAccess) {
      push({
        stopIdx: i,
        boardings: 0,
        timeMs: departMs + km * accessMinPerKm * 60_000,
        waitMin: 0,
        prev: null,
        via: { type: 'access', km },
      });
    }
  }
  if (open.length === 0) return null;

  let bestFinal: { label: Label; egressKm: number; arriveMs: number } | null = null;

  while (open.length > 0) {
    const cur = popMin();
    if (cur.timeMs > best[cur.stopIdx][cur.boardings]) continue;
    if (bestFinal && cur.timeMs >= bestFinal.arriveMs) continue;

    const curStop = stops[cur.stopIdx];

    // Výstup: chůze do cíle — jen pokud jsme už jeli (jinak je to čistá chůze, ne MHD).
    if (cur.boardings > 0) {
      const egKm = haversineKm(curStop, destination);
      const arr = cur.timeMs + egKm * walkMinPerKm * 60_000;
      if (!bestFinal || arr < bestFinal.arriveMs) {
        bestFinal = { label: cur, egressKm: egKm, arriveMs: arr };
      }
    }

    // Nástup na linky.
    if (cur.boardings < maxBoard) {
      const transferPenaltyMs = cur.boardings > 0 ? net.transferMin * 60_000 : 0;
      const readyMs = cur.timeMs + transferPenaltyMs;
      for (const { run, pos } of runsAtStop[cur.stopIdx]) {
        let depsAtFirst = lineDeps.get(run.line.id);
        if (!depsAtFirst) {
          depsAtFirst = lineDepartureMinutes(run.line);
          lineDeps.set(run.line.id, depsAtFirst);
        }
        if (depsAtFirst.length === 0) continue;
        // Normalizované a seřazené odjezdy z této pozice — rotací, jen jednou.
        const cacheKey = `${runKeys.get(run)}:${pos}`;
        let depsHere = depsAtPos.get(cacheKey);
        if (!depsHere) {
          depsHere = rotateDeps(depsAtFirst, Math.round(run.offsetsMin[pos]));
          depsAtPos.set(cacheKey, depsHere);
        }
        const depMs = fastNextDeparture(readyMs, depsHere);
        if (depMs === null) continue;
        const waitBefore = (depMs - cur.timeMs) / 60_000;
        for (let k = pos + 1; k < run.stops.length; k++) {
          const arrMs = depMs + (run.offsetsMin[k] - run.offsetsMin[pos]) * 60_000;
          const toIdx = idxOf.get(run.stops[k].id);
          if (toIdx === undefined) continue;
          push({
            stopIdx: toIdx,
            boardings: cur.boardings + 1,
            timeMs: arrMs,
            waitMin: cur.waitMin + waitBefore,
            prev: cur,
            via: { type: 'ride', run, boardIdx: pos, alightIdx: k, depMs, waitBefore },
          });
        }
      }
    }

    // Pěší přesun mezi blízkými zastávkami (přestup pěšky).
    for (const e of walkEdges[cur.stopIdx]) {
      push({
        stopIdx: e.to,
        boardings: cur.boardings,
        timeMs: cur.timeMs + e.km * walkMinPerKm * 60_000,
        waitMin: cur.waitMin,
        prev: cur,
        via: { type: 'walk', fromIdx: cur.stopIdx, km: e.km },
      });
    }
  }

  if (!bestFinal) return null;
  return reconstruct(bestFinal.label, bestFinal.egressKm, origin, destination, departMs, stops, opts);
}

function reconstruct(
  label: Label,
  egressKm: number,
  origin: LatLng,
  destination: LatLng,
  departMs: number,
  stops: TransitStop[],
  opts: { walkSpeedKmh: number; walkCircuity: number },
): PtJourney {
  const chain: Label[] = [];
  for (let l: Label | null = label; l; l = l.prev) chain.unshift(l);

  const legs: PtLeg[] = [];
  let waitMin = 0;
  let rideKm = 0;
  let accessKm = 0;
  const linesUsed: TransitLine[] = [];

  for (const l of chain) {
    if (!l.via) continue;
    if (l.via.type === 'access') {
      accessKm = l.via.km;
      legs.push({
        kind: 'access',
        toStop: stops[l.stopIdx],
        startMs: departMs,
        endMs: l.timeMs,
        distanceKm: l.via.km,
      });
    } else if (l.via.type === 'walk') {
      legs.push({
        kind: 'transfer',
        fromStop: stops[l.via.fromIdx],
        toStop: stops[l.stopIdx],
        startMs: l.prev!.timeMs,
        endMs: l.timeMs,
        distanceKm: l.via.km,
      });
    } else {
      const v = l.via;
      const board = v.run.stops[v.boardIdx];
      const alight = v.run.stops[v.alightIdx];
      if (v.waitBefore > 0.01) {
        waitMin += v.waitBefore;
        legs.push({
          kind: 'wait',
          fromStop: board,
          toStop: board,
          line: v.run.line,
          startMs: v.depMs - v.waitBefore * 60_000,
          endMs: v.depMs,
          distanceKm: 0,
        });
      }
      const km = v.run.offsetsKm[v.alightIdx] - v.run.offsetsKm[v.boardIdx];
      rideKm += km;
      if (!linesUsed.some((x) => x.id === v.run.line.id)) linesUsed.push(v.run.line);
      legs.push({
        kind: 'ride',
        line: v.run.line,
        fromStop: board,
        toStop: alight,
        viaStops: v.run.stops.slice(v.boardIdx, v.alightIdx + 1),
        startMs: v.depMs,
        endMs: l.timeMs,
        distanceKm: km,
      });
    }
  }

  const lastStop = stops[label.stopIdx];
  const walkMinPerKm = (60 / opts.walkSpeedKmh) * opts.walkCircuity;
  const arriveMs = label.timeMs + egressKm * walkMinPerKm * 60_000;
  legs.push({
    kind: 'egress',
    fromStop: lastStop,
    startMs: label.timeMs,
    endMs: arriveMs,
    distanceKm: egressKm,
  });

  const rides = legs.filter((l) => l.kind === 'ride');
  return {
    legs,
    departMs,
    arriveMs,
    waitMin,
    rideKm,
    accessKm,
    egressKm,
    transfers: Math.max(0, rides.length - 1),
    linesUsed,
    accessible: linesUsed.every((l) => l.accessible),
  };
}

/* ------------------------------------------------------------------ */
/* Očekávaný model pro zóny (časově nezávislý, hromadné výpočty)        */
/* ------------------------------------------------------------------ */

export interface ExpectedPtField {
  /** Očekávaný čas (min) ze zastávky i do cíle vč. výstupní chůze. */
  minutesFromStop: Float64Array;
  stops: TransitStop[];
  /** Očekávané čekání (min) započtené při nástupu na zastávce i. */
  boardWait: Float64Array;
  /** Počet nástupů na cestě ze zastávky i do cíle (přestupy = nástupy − 1). */
  boardings: Float64Array;
  /** Prostorový index zastávek pro rychlé dotazy z buněk mřížky. */
  index: StopIndex;
}

/** Indexy zastávek pole do radiusKm od bodu (pro zónové výpočty). */
export function fieldNearbyStops(field: ExpectedPtField, p: LatLng, radiusKm: number): number[] {
  return nearbyStops(p, field.stops, field.index, radiusKm);
}

/**
 * Předpočet: očekávaný čas z každé zastávky do cíle D (jízda + přestupy
 * + očekávaná čekání = polovina intervalu). Jeden výpočet, pak lze
 * vyhodnotit tisíce buněk mřížky jako walk(buňka→zastávka) + pole[zastávka].
 */
export function buildExpectedField(
  destination: LatLng,
  net: TransitNetwork,
  walkSpeedKmh: number,
  walkCircuity: number,
  /** Čas odjezdu (ms) — čekání dle jízdního řádu v daném čase; undefined = časově nezávislý průměr. */
  departMs?: number,
): ExpectedPtField | null {
  if (net.stops.length === 0 || net.lines.length === 0) return null;
  const runs = buildRuns(net);
  if (runs.length === 0) return null;
  const stops = net.stops;
  const idxOf = new Map(stops.map((s, i) => [s.id, i]));
  const walkMinPerKm = (60 / walkSpeedKmh) * walkCircuity;

  // Hrany pozpátku: čas do cíle. dist[i] = očekávané minuty ze zastávky i do D.
  const dist = new Float64Array(stops.length).fill(Infinity);
  const boardWait = new Float64Array(stops.length).fill(0);
  const boardings = new Float64Array(stops.length).fill(0);
  for (let i = 0; i < stops.length; i++) {
    dist[i] = haversineKm(stops[i], destination) * walkMinPerKm;
  }

  // Bellman–Ford styl: relaxujeme jízdní úseky s očekávaným čekáním při
  // nástupu + přestupní chůzi. Přestupní hrany se předpočítají JEDNOU přes
  // prostorový index (u PID by O(n²) v každé iteraci znamenalo desítky
  // milionů výpočtů) — pak je každá iterace lineární vůči počtu hran.
  const minOfDay = departMs !== undefined ? pragueMinutesOfDay(departMs) : null;
  const expWaitConst = new Map<string, number>(net.lines.map((l) => [l.id, expectedWaitMin(l)]));
  // Čekání závisí na NÁSTUPNÍ zastávce: odjezd ze zastávky s offsetem o je
  // (odjezd z výchozí) + o, takže čekání v čase t = čekání(t − o) na výchozí.
  // Bez korekce by linky s explicitními odjezdy (PID!) vycházely v zónách
  // absurdně dlouhé a „vyhrávala" by pěší chůze.
  const waitCache = new Map<string, number>();
  const waitAt = (line: TransitLine, offsetMin: number): number => {
    if (minOfDay === null) return expWaitConst.get(line.id) ?? 7;
    const off = Math.round(offsetMin);
    const key = `${line.id}:${off}`;
    let w = waitCache.get(key);
    if (w === undefined) {
      w = expectedWaitAtMin(line, minOfDay - off);
      waitCache.set(key, w);
    }
    return w;
  };

  const spatial = buildStopIndex(stops);
  const transferEdges: Array<Array<{ to: number; km: number }>> = stops.map(() => []);
  for (let i = 0; i < stops.length; i++) {
    for (const j of nearbyStops(stops[i], stops, spatial, WALK_TRANSFER_KM)) {
      if (j === i) continue;
      const km = haversineKm(stops[i], stops[j]);
      if (km <= WALK_TRANSFER_KM) transferEdges[i].push({ to: j, km });
    }
  }

  for (let iter = 0; iter < stops.length; iter++) {
    let changed = false;
    for (const run of runs) {
      for (let i = 0; i < run.stops.length - 1; i++) {
        const fromIdx = idxOf.get(run.stops[i].id);
        if (fromIdx === undefined) continue;
        const w = waitAt(run.line, run.offsetsMin[i]);
        for (let k = i + 1; k < run.stops.length; k++) {
          const toIdx = idxOf.get(run.stops[k].id);
          if (toIdx === undefined) continue;
          const ride = run.offsetsMin[k] - run.offsetsMin[i];
          const cand = w + ride + dist[toIdx];
          if (cand < dist[fromIdx] - 1e-9) {
            dist[fromIdx] = cand;
            boardWait[fromIdx] = w;
            boardings[fromIdx] = boardings[toIdx] + 1;
            changed = true;
          }
        }
      }
    }
    // přestupní pěší hrany (předpočítané, řídké)
    for (let i = 0; i < stops.length; i++) {
      for (const e of transferEdges[i]) {
        const cand = e.km * walkMinPerKm + net.transferMin + dist[e.to];
        if (cand < dist[i] - 1e-9) {
          dist[i] = cand;
          boardWait[i] = boardWait[e.to];
          boardings[i] = boardings[e.to];
          changed = true;
        }
      }
    }
    if (!changed) break;
  }

  return { minutesFromStop: dist, stops, boardWait, boardings, index: spatial };
}

/**
 * Očekávaný čas z libovolného bodu P do cíle přes MHD (min),
 * včetně docházky na zastávku. Vrací Infinity, když MHD nedává smysl.
 */
export function expectedPtMinutes(
  p: LatLng,
  field: ExpectedPtField,
  accessSpeedKmh: number,
  accessCircuity: number,
  maxAccessKm: number,
): { minutes: number; wait: number; accessKm: number } {
  const accessMinPerKm = (60 / accessSpeedKmh) * accessCircuity;
  let bestMin = Infinity;
  let bestWait = 0;
  let bestAccessKm = Infinity;
  // Jen zastávky v dosahu (prostorový index) — klíčové pro tisíce buněk × tisíce zastávek.
  for (const i of nearbyStops(p, field.stops, field.index, maxAccessKm)) {
    const km = haversineKm(p, field.stops[i]);
    if (km > maxAccessKm) continue;
    const t = km * accessMinPerKm + field.minutesFromStop[i];
    if (t < bestMin) {
      bestMin = t;
      bestWait = field.boardWait[i];
      bestAccessKm = km;
    }
  }
  return { minutes: bestMin, wait: bestWait, accessKm: bestAccessKm };
}

/** Pomůcka pro ruční editor: nová prázdná linka. */
export function createManualLine(stopIds: string[] = []): TransitLine {
  return {
    id: `line-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e4)}`,
    name: 'Nová linka',
    vehicle: 'bus',
    color: '#1d7ff0',
    stopIds,
    headwayMin: 10,
    firstDep: '05:00',
    lastDep: '23:00',
    speedKmh: 22,
    dwellMin: 0.5,
    costFlat: 30,
    emissionsPerKm: 60,
    comfort: 3,
    accessible: true,
    bidirectional: true,
  };
}

/** Hrubá interpolovaná geometrie jízdního úseku přes zastávky. */
export function rideGeometry(viaStops: TransitStop[]): LatLng[] {
  return viaStops.map((s) => ({ lat: s.lat, lng: s.lng }));
}
